import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    ChevronDown, ChevronRight, User, LogOut, Settings as SettingsIcon, Users, Plus, Edit, Trash2,
    Search, Calendar, Clock, Bell, Heart, Upload, X, Crown, Download, Share2,
    AlertCircle, CheckCircle, Eye, FileText, Syringe, Stethoscope, Utensils,
    Scissors, Activity, Droplet, Home, Phone, Mail, MapPin, Shield, Award,
    Printer, Dog, Cat, Bird, Fish, Image as ImageIcon
} from 'lucide-react';
import { fetchPets, createPet, updatePet, deletePet } from '../services/petsApi';
import MemberAvatar from './MemberAvatar';
import MemberSelector from './MemberSelector';
import Premium from './Premium';

const Pets = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('pets'); // pets, vet, feeding, sitter
    const [showFamilyDropdown, setShowFamilyDropdown] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showPremiumModal, setShowPremiumModal] = useState(false);
    const [families, setFamilies] = useState([]);
    const [selectedFamily, setSelectedFamily] = useState(null);
    const dropdownRef = useRef(null);
    const userMenuRef = useRef(null);
    const photoInputRef = useRef(null);

    // Pets state
    const [pets, setPets] = useState([]);
    const [selectedPet, setSelectedPet] = useState(null);
    const [showAddPetModal, setShowAddPetModal] = useState(false);
    const [editingPet, setEditingPet] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [newPet, setNewPet] = useState({
        name: '',
        species: 'Dog',
        breed: '',
        age: '',
        gender: 'Male',
        microchipId: '',
        allergies: '',
        insuranceInfo: '',
        photo: null,
        weight: '',
        color: ''
    });

    // Vet Records state
    const [vetRecords, setVetRecords] = useState([]);
    const [showAddVetModal, setShowAddVetModal] = useState(false);
    const [editingVetRecord, setEditingVetRecord] = useState(null);
    const [newVetRecord, setNewVetRecord] = useState({
        petId: '',
        visitDate: new Date().toISOString().split('T')[0],
        vetName: '',
        reason: '',
        notes: '',
        attachments: [],
        type: 'checkup',
        prescriptions: '',
        nextVisit: ''
    });

    // Vaccinations state
    const [vaccinations, setVaccinations] = useState([]);
    const [showAddVaccineModal, setShowAddVaccineModal] = useState(false);
    const [newVaccination, setNewVaccination] = useState({
        petId: '',
        vaccineName: '',
        dateGiven: new Date().toISOString().split('T')[0],
        nextDue: '',
        veterinarian: '',
        notes: ''
    });

    // Feeding Schedule state
    const [feedingSchedules, setFeedingSchedules] = useState([]);
    const [showAddFeedingModal, setShowAddFeedingModal] = useState(false);
    const [newFeeding, setNewFeeding] = useState({
        petId: '',
        time: '',
        foodType: '',
        quantity: '',
        assignedTo: '',
        recurrence: 'Daily',
        notes: ''
    });

    // Care Tasks state
    const [careTasks, setCareTasks] = useState([]);
    const [showAddCareTaskModal, setShowAddCareTaskModal] = useState(false);
    const [newCareTask, setNewCareTask] = useState({
        petId: '',
        taskType: 'Walk',
        time: '',
        duration: '',
        assignedTo: '',
        recurrence: 'Daily',
        completed: false
    });

    // Sitter Notes state
    const [sitterNotes, setSitterNotes] = useState({});
    const [editingSitterNotes, setEditingSitterNotes] = useState(false);

    // Load data
    useEffect(() => {
        loadFamilies();
        loadVetRecords();
        loadVaccinations();
        loadFeedingSchedules();
        loadCareTasks();
        loadSitterNotes();
    }, []);

    // Load pets when family changes
    useEffect(() => {
        if (selectedFamily) {
            loadPets();
        }
    }, [selectedFamily]);

    const loadFamilies = async () => {
        try {
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
            const response = await fetch(`${apiUrl}/v1/families`, {
                credentials: 'include'
            });
            
            if (!response.ok) {
                throw new Error('Failed to load families');
            }
            
            const familiesData = await response.json();
            setFamilies(familiesData);
            
            const selectedFamilyId = localStorage.getItem('selectedFamilyId');
            let familyToSelect = null;
            
            if (selectedFamilyId && familiesData.length > 0) {
                familyToSelect = familiesData.find(f => f.id === selectedFamilyId);
            }
            
            if (!familyToSelect && familiesData.length === 1) {
                familyToSelect = familiesData[0];
                localStorage.setItem('selectedFamilyId', familiesData[0].id);
            }
            
            if (!familyToSelect && familiesData.length > 0) {
                localStorage.removeItem('selectedFamilyId');
            }
            
            setSelectedFamily(familyToSelect);
        } catch (error) {
            console.error('Error loading families:', error);
        }
    };

    const loadPets = async () => {
        if (!selectedFamily) return;
        
        try {
            const response = await fetchPets(selectedFamily.id);
            const petsData = response.items || [];
            setPets(petsData);
            
            if (petsData.length > 0 && !selectedPet) {
                setSelectedPet(petsData[0]);
            }
        } catch (error) {
            console.error('Error loading pets:', error);
        }
    };

    const loadVetRecords = () => {
        const stored = localStorage.getItem('vetRecords');
        if (stored) setVetRecords(JSON.parse(stored));
    };

    const loadVaccinations = () => {
        const stored = localStorage.getItem('vaccinations');
        if (stored) setVaccinations(JSON.parse(stored));
    };

    const loadFeedingSchedules = () => {
        const stored = localStorage.getItem('feedingSchedules');
        if (stored) setFeedingSchedules(JSON.parse(stored));
    };

    const loadCareTasks = () => {
        const stored = localStorage.getItem('careTasks');
        if (stored) setCareTasks(JSON.parse(stored));
    };

    const loadSitterNotes = () => {
        const stored = localStorage.getItem('sitterNotes');
        if (stored) setSitterNotes(JSON.parse(stored));
    };

    // Removed savePets - now using API directly

    const saveVetRecords = (data) => {
        localStorage.setItem('vetRecords', JSON.stringify(data));
        setVetRecords(data);
    };

    const saveVaccinations = (data) => {
        localStorage.setItem('vaccinations', JSON.stringify(data));
        setVaccinations(data);
    };

    const saveFeedingSchedules = (data) => {
        localStorage.setItem('feedingSchedules', JSON.stringify(data));
        setFeedingSchedules(data);
    };

    const saveCareTasks = (data) => {
        localStorage.setItem('careTasks', JSON.stringify(data));
        setCareTasks(data);
    };

    const saveSitterNotes = (data) => {
        localStorage.setItem('sitterNotes', JSON.stringify(data));
        setSitterNotes(data);
    };

    const handleFamilySelect = (family) => {
        setSelectedFamily(family);
        localStorage.setItem('selectedFamilyId', family.id);
        setShowFamilyDropdown(false);
    };

    const handleLogout = () => {
        localStorage.removeItem('hasLoggedIn');
        localStorage.removeItem('selectedFamilyId');
        navigate('/login');
    };

    // Click outside handlers
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowFamilyDropdown(false);
            }
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setShowUserMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Pet functions
    const handlePhotoSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                alert('Photo size must be less than 5MB');
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                setNewPet({ ...newPet, photo: reader.result });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleAddPet = async () => {
        if (!newPet.name.trim()) {
            alert('Please enter pet name');
            return;
        }

        if (!selectedFamily) {
            alert('Please select a family first');
            return;
        }

        try {
            // Map frontend fields to backend schema
            const petData = {
                name: newPet.name,
                species: newPet.species || null,
                breed: newPet.breed || null,
                date_of_birth: newPet.age ? new Date(new Date().setFullYear(new Date().getFullYear() - parseInt(newPet.age))).toISOString().split('T')[0] : null,
                vet_name: null,
                vet_contact: null,
                microchip_id: newPet.microchipId || null,
                feeding_schedule: null,
                medication_notes: newPet.allergies || null,
                sitter_notes: null,
                photo_url: newPet.photo || null
            };

            const createdPet = await createPet(selectedFamily.id, petData);
            setPets([...pets, createdPet]);
            
            if (!selectedPet) {
                setSelectedPet(createdPet);
            }

            resetPetForm();
            setShowAddPetModal(false);
        } catch (error) {
            console.error('Error creating pet:', error);
            alert(`Failed to create pet: ${error.message}`);
        }
    };

    const handleUpdatePet = async () => {
        if (!newPet.name.trim()) {
            alert('Please enter pet name');
            return;
        }

        if (!selectedFamily) {
            alert('Please select a family first');
            return;
        }

        try {
            // Map frontend fields to backend schema
            const petData = {
                name: newPet.name,
                species: newPet.species || null,
                breed: newPet.breed || null,
                date_of_birth: newPet.age ? new Date(new Date().setFullYear(new Date().getFullYear() - parseInt(newPet.age))).toISOString().split('T')[0] : null,
                vet_name: null,
                vet_contact: null,
                microchip_id: newPet.microchipId || null,
                feeding_schedule: null,
                medication_notes: newPet.allergies || null,
                sitter_notes: null,
                photo_url: newPet.photo || null
            };

            const updatedPet = await updatePet(selectedFamily.id, editingPet.id, petData);
            const updated = pets.map(p => p.id === editingPet.id ? updatedPet : p);
            setPets(updated);
            
            if (selectedPet?.id === editingPet.id) {
                setSelectedPet(updatedPet);
            }

            resetPetForm();
            setShowAddPetModal(false);
            setEditingPet(null);
        } catch (error) {
            console.error('Error updating pet:', error);
            alert(`Failed to update pet: ${error.message}`);
        }
    };

    const handleDeletePet = async (id) => {
        if (!window.confirm('Are you sure you want to delete this pet?')) {
            return;
        }

        if (!selectedFamily) {
            alert('Please select a family first');
            return;
        }

        try {
            await deletePet(selectedFamily.id, id);
            const filtered = pets.filter(p => p.id !== id);
            setPets(filtered);
            
            if (selectedPet?.id === id) {
                setSelectedPet(filtered[0] || null);
            }
        } catch (error) {
            console.error('Error deleting pet:', error);
            alert(`Failed to delete pet: ${error.message}`);
        }
    };

    const resetPetForm = () => {
        setNewPet({
            name: '',
            species: 'Dog',
            breed: '',
            age: '',
            gender: 'Male',
            microchipId: '',
            allergies: '',
            insuranceInfo: '',
            photo: null,
            weight: '',
            color: ''
        });
    };

    // Vet record functions
    const handleAddVetRecord = () => {
        if (!newVetRecord.petId || !newVetRecord.vetName) {
            alert('Please select a pet and enter vet name');
            return;
        }

        const record = {
            ...newVetRecord,
            id: Date.now(),
            createdAt: new Date().toISOString()
        };

        saveVetRecords([...vetRecords, record]);
        setNewVetRecord({
            petId: selectedPet?.id || '',
            visitDate: new Date().toISOString().split('T')[0],
            vetName: '',
            reason: '',
            notes: '',
            attachments: [],
            type: 'checkup',
            prescriptions: '',
            nextVisit: ''
        });
        setShowAddVetModal(false);
    };

    const handleDeleteVetRecord = (id) => {
        if (window.confirm('Delete this vet record?')) {
            saveVetRecords(vetRecords.filter(r => r.id !== id));
        }
    };

    // Vaccination functions
    const handleAddVaccination = () => {
        if (!newVaccination.petId || !newVaccination.vaccineName) {
            alert('Please select a pet and enter vaccine name');
            return;
        }

        const vaccination = {
            ...newVaccination,
            id: Date.now()
        };

        saveVaccinations([...vaccinations, vaccination]);
        setNewVaccination({
            petId: selectedPet?.id || '',
            vaccineName: '',
            dateGiven: new Date().toISOString().split('T')[0],
            nextDue: '',
            veterinarian: '',
            notes: ''
        });
        setShowAddVaccineModal(false);
    };

    const handleDeleteVaccination = (id) => {
        if (window.confirm('Delete this vaccination record?')) {
            saveVaccinations(vaccinations.filter(v => v.id !== id));
        }
    };

    // Feeding functions
    const handleAddFeeding = () => {
        if (!newFeeding.petId || !newFeeding.time) {
            alert('Please select a pet and set feeding time');
            return;
        }

        const feeding = {
            ...newFeeding,
            id: Date.now()
        };

        saveFeedingSchedules([...feedingSchedules, feeding]);
        setNewFeeding({
            petId: selectedPet?.id || '',
            time: '',
            foodType: '',
            quantity: '',
            assignedTo: '',
            recurrence: 'Daily',
            notes: ''
        });
        setShowAddFeedingModal(false);
    };

    const handleDeleteFeeding = (id) => {
        if (window.confirm('Delete this feeding schedule?')) {
            saveFeedingSchedules(feedingSchedules.filter(f => f.id !== id));
        }
    };

    // Care task functions
    const handleAddCareTask = () => {
        if (!newCareTask.petId || !newCareTask.time) {
            alert('Please select a pet and set time');
            return;
        }

        const task = {
            ...newCareTask,
            id: Date.now()
        };

        saveCareTasks([...careTasks, task]);
        setNewCareTask({
            petId: selectedPet?.id || '',
            taskType: 'Walk',
            time: '',
            duration: '',
            assignedTo: '',
            recurrence: 'Daily',
            completed: false
        });
        setShowAddCareTaskModal(false);
    };

    const handleToggleCareTask = (id) => {
        const updated = careTasks.map(t => 
            t.id === id ? { ...t, completed: !t.completed } : t
        );
        saveCareTasks(updated);
    };

    const handleDeleteCareTask = (id) => {
        if (window.confirm('Delete this care task?')) {
            saveCareTasks(careTasks.filter(t => t.id !== id));
        }
    };

    // Sitter notes functions
    const handleSaveSitterNotes = () => {
        saveSitterNotes(sitterNotes);
        setEditingSitterNotes(false);
        alert('Sitter notes saved successfully!');
    };

    // Export Pet Passport
    const handleExportPetPassport = (pet) => {
        alert(`Exporting Pet Passport for ${pet.name}... (PDF generation would happen here)`);
    };

    // Filter pets
    const filteredPets = pets.filter(pet =>
        pet.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        pet.breed.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Get data for selected pet
    const selectedPetVetRecords = vetRecords.filter(r => r.petId === selectedPet?.id);
    const selectedPetVaccinations = vaccinations.filter(v => v.petId === selectedPet?.id);
    const selectedPetFeeding = feedingSchedules.filter(f => f.petId === selectedPet?.id);
    const selectedPetCareTasks = careTasks.filter(t => t.petId === selectedPet?.id);

    const species = ['Dog', 'Cat', 'Bird', 'Fish', 'Rabbit', 'Hamster', 'Guinea Pig', 'Other'];
    const visitTypes = ['checkup', 'vaccination', 'surgery', 'emergency', 'grooming', 'dental'];
    const careTaskTypes = ['Walk', 'Grooming', 'Medication', 'Play Time', 'Training', 'Bath'];

    return (
        <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-pink-50">
            {/* Navigation Bar */}
            <nav className="bg-white shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center gap-4">
                            <button 
                                onClick={() => navigate('/dashboard')}
                                className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
                            >
                                <img src="/logo.png" alt="mifamli logo" className="h-10 w-10" />
                                <span className="text-xl font-bold text-primary-blue">mifamli</span>
                            </button>

                            {/* Mi Famlis Dropdown */}
                            <div className="relative" ref={dropdownRef}>
                                <button
                                    onClick={() => setShowFamilyDropdown(!showFamilyDropdown)}
                                    className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-white shadow-md hover:shadow-lg transition-all"
                                    style={{
                                        background: 'linear-gradient(135deg, #3b82f6 0%, #ec4899 100%)'
                                    }}
                                >
                                    {selectedFamily ? (
                                        <>
                                            <img
                                                src={selectedFamily.photo}
                                                alt={selectedFamily.name}
                                                className="w-6 h-6 rounded-full object-cover"
                                            />
                                            <span>{selectedFamily.name}</span>
                                        </>
                                    ) : (
                                        <span>Mi Famlis</span>
                                    )}
                                    <ChevronDown className="w-4 h-4" />
                                </button>

                                {showFamilyDropdown && (
                                    <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl py-2 border border-gray-200 z-50">
                                        {families.map((family) => (
                                            <button
                                                key={family.id}
                                                onClick={() => handleFamilySelect(family)}
                                                className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-3"
                                            >
                                                {family.photo ? (
                                                    <img 
                                                        src={family.photo} 
                                                        alt={family.name}
                                                        className="w-8 h-8 rounded-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-pink-400 flex items-center justify-center">
                                                        <Users className="w-4 h-4 text-white" />
                                                    </div>
                                                )}
                                                <div>
                                                    <div className="font-medium">{family.name}</div>
                                                    {family.tagline && (
                                                        <div className="text-xs text-gray-500">{family.tagline}</div>
                                                    )}
                                                </div>
                                            </button>
                                        ))}
                                        <div className="border-t border-gray-200 mt-2 pt-2">
                                            <button
                                                onClick={() => navigate('/famlis')}
                                                className="w-full px-4 py-2 text-left hover:bg-gray-50 text-primary-blue font-medium"
                                            >
                                                + Add Family
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            {/* User Avatar Menu */}
                            <div className="relative" ref={userMenuRef}>
                                <button
                                    onClick={() => setShowUserMenu(!showUserMenu)}
                                    className="flex items-center gap-2 hover:bg-gray-50 rounded-lg p-2 transition-colors"
                                >
                                    <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-primary-blue to-primary-pink flex items-center justify-center text-white font-semibold">
                                        U
                                    </div>
                                </button>

                                {showUserMenu && (
                                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl py-1 border border-gray-100 z-50">
                                        <button className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700">
                                            <User className="w-5 h-5" />
                                            <span className="font-medium">Profile</span>
                                        </button>
                                        <button className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700">
                                            <SettingsIcon className="w-5 h-5" />
                                            <span className="font-medium">Settings</span>
                                        </button>
                                        <button
                                            onClick={() => setShowPremiumModal(true)}
                                            className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                        >
                                            <Crown className="w-5 h-5 text-yellow-500" />
                                            <span className="font-medium">Upgrade to Premium</span>
                                        </button>
                                        <div className="border-t border-gray-100 my-1"></div>
                                        <button
                                            onClick={handleLogout}
                                            className="w-full px-4 py-3 text-left hover:bg-red-50 flex items-center gap-3 text-red-600"
                                        >
                                            <LogOut className="w-5 h-5" />
                                            <span className="font-medium">Logout</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Breadcrumb */}
                <div className="mb-4 flex items-center gap-2 text-sm">
                    <button 
                        onClick={() => navigate('/dashboard')}
                        className="text-gray-600 hover:text-primary-blue transition-colors"
                    >
                        Dashboard
                    </button>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-900 font-medium">Pets</span>
                </div>

                {/* Header */}
                <div className="mb-8 flex justify-between items-center">
                    <div>
                        <h1 className="text-4xl font-bold text-gray-900 mb-2">🐾 Pets</h1>
                        <p className="text-gray-600">Care for your furry (and feathered) family members</p>
                    </div>
                    <button
                        onClick={() => {
                            resetPetForm();
                            setEditingPet(null);
                            setShowAddPetModal(true);
                        }}
                        className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-orange-600 to-pink-600 text-white rounded-lg hover:opacity-90"
                    >
                        <Plus className="w-5 h-5" />
                        <span>Add Pet</span>
                    </button>
                </div>

                {pets.length === 0 ? (
                    <div className="text-center py-20 bg-white rounded-lg shadow-md">
                        <Dog className="w-20 h-20 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-gray-700 mb-2">No pets yet</h3>
                        <p className="text-gray-500 mb-6">Add your first furry friend to get started!</p>
                        <button
                            onClick={() => setShowAddPetModal(true)}
                            className="px-6 py-3 bg-gradient-to-r from-orange-600 to-pink-600 text-white rounded-lg hover:opacity-90 font-semibold"
                        >
                            Add Your First Pet
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                        {/* Pet Sidebar */}
                        <div className="lg:col-span-1">
                            <div className="bg-white rounded-lg shadow-md p-4">
                                <div className="relative mb-4">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                    <input
                                        type="text"
                                        placeholder="Search pets..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                    />
                                </div>

                                <div className="space-y-2">
                                    {filteredPets.map(pet => (
                                        <div
                                            key={pet.id}
                                            onClick={() => setSelectedPet(pet)}
                                            className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors ${
                                                selectedPet?.id === pet.id
                                                    ? 'bg-orange-100 border-2 border-orange-500'
                                                    : 'hover:bg-gray-50 border-2 border-transparent'
                                            }`}
                                        >
                                            {pet.photo ? (
                                                <img
                                                    src={pet.photo}
                                                    alt={pet.name}
                                                    className="w-12 h-12 rounded-full object-cover"
                                                />
                                            ) : (
                                                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-400 to-pink-400 flex items-center justify-center">
                                                    {pet.species === 'Dog' && <Dog className="w-6 h-6 text-white" />}
                                                    {pet.species === 'Cat' && <Cat className="w-6 h-6 text-white" />}
                                                    {pet.species === 'Bird' && <Bird className="w-6 h-6 text-white" />}
                                                    {pet.species === 'Fish' && <Fish className="w-6 h-6 text-white" />}
                                                    {!['Dog', 'Cat', 'Bird', 'Fish'].includes(pet.species) && <Heart className="w-6 h-6 text-white" />}
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <p className="font-semibold text-gray-900 truncate">{pet.name}</p>
                                                <p className="text-xs text-gray-600">{pet.species} • {pet.age || 'Age unknown'}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Main Content */}
                        <div className="lg:col-span-3">
                            {selectedPet && (
                                <>
                                    {/* Pet Profile Card */}
                                    <div className="bg-gradient-to-r from-orange-500 to-pink-500 rounded-lg shadow-lg p-6 text-white mb-6">
                                        <div className="flex items-start justify-between">
                                            <div className="flex items-center space-x-4">
                                                {selectedPet.photo ? (
                                                    <img
                                                        src={selectedPet.photo}
                                                        alt={selectedPet.name}
                                                        className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-lg"
                                                    />
                                                ) : (
                                                    <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center">
                                                        {selectedPet.species === 'Dog' && <Dog className="w-10 h-10 text-orange-500" />}
                                                        {selectedPet.species === 'Cat' && <Cat className="w-10 h-10 text-orange-500" />}
                                                        {selectedPet.species === 'Bird' && <Bird className="w-10 h-10 text-orange-500" />}
                                                        {selectedPet.species === 'Fish' && <Fish className="w-10 h-10 text-orange-500" />}
                                                    </div>
                                                )}
                                                <div>
                                                    <h2 className="text-3xl font-bold mb-1">{selectedPet.name}</h2>
                                                    <p className="text-lg opacity-90">{selectedPet.breed || selectedPet.species}</p>
                                                    <div className="flex items-center space-x-4 mt-2 text-sm">
                                                        {selectedPet.age && <span>🎂 {selectedPet.age}</span>}
                                                        {selectedPet.gender && <span>♂️ {selectedPet.gender}</span>}
                                                        {selectedPet.weight && <span>⚖️ {selectedPet.weight}</span>}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex space-x-2">
                                                <button
                                                    onClick={() => handleExportPetPassport(selectedPet)}
                                                    className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                                                    title="Export Pet Passport"
                                                >
                                                    <Download className="w-5 h-5" />
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setEditingPet(selectedPet);
                                                        setNewPet(selectedPet);
                                                        setShowAddPetModal(true);
                                                    }}
                                                    className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                                                >
                                                    <Edit className="w-5 h-5" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeletePet(selectedPet.id)}
                                                    className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                                                >
                                                    <Trash2 className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Quick Stats */}
                                        <div className="grid grid-cols-3 gap-4 mt-6">
                                            <div className="bg-white/20 rounded-lg p-3 backdrop-blur-sm">
                                                <p className="text-xs opacity-80 mb-1">Next Vet Visit</p>
                                                <p className="font-semibold">
                                                    {selectedPetVetRecords.find(r => r.nextVisit)?.nextVisit 
                                                        ? new Date(selectedPetVetRecords.find(r => r.nextVisit).nextVisit).toLocaleDateString()
                                                        : 'Not scheduled'}
                                                </p>
                                            </div>
                                            <div className="bg-white/20 rounded-lg p-3 backdrop-blur-sm">
                                                <p className="text-xs opacity-80 mb-1">Vaccinations</p>
                                                <p className="font-semibold">{selectedPetVaccinations.length} recorded</p>
                                            </div>
                                            <div className="bg-white/20 rounded-lg p-3 backdrop-blur-sm">
                                                <p className="text-xs opacity-80 mb-1">Microchip</p>
                                                <p className="font-semibold text-xs">{selectedPet.microchipId || 'Not registered'}</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Tabs */}
                                    <div className="mb-6 border-b border-gray-200 bg-white rounded-t-lg px-4">
                                        <div className="flex space-x-8 overflow-x-auto">
                                            <button
                                                onClick={() => setActiveTab('pets')}
                                                className={`pb-4 px-2 font-medium transition-colors relative whitespace-nowrap ${
                                                    activeTab === 'pets' ? 'text-orange-600' : 'text-gray-500 hover:text-gray-700'
                                                }`}
                                            >
                                                🐾 Overview
                                                {activeTab === 'pets' && (
                                                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-600"></div>
                                                )}
                                            </button>
                                            <button
                                                onClick={() => setActiveTab('vet')}
                                                className={`pb-4 px-2 font-medium transition-colors relative whitespace-nowrap ${
                                                    activeTab === 'vet' ? 'text-orange-600' : 'text-gray-500 hover:text-gray-700'
                                                }`}
                                            >
                                                🩺 Vet Records
                                                {activeTab === 'vet' && (
                                                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-600"></div>
                                                )}
                                            </button>
                                            <button
                                                onClick={() => setActiveTab('feeding')}
                                                className={`pb-4 px-2 font-medium transition-colors relative whitespace-nowrap ${
                                                    activeTab === 'feeding' ? 'text-orange-600' : 'text-gray-500 hover:text-gray-700'
                                                }`}
                                            >
                                                🍖 Feeding & Care
                                                {activeTab === 'feeding' && (
                                                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-600"></div>
                                                )}
                                            </button>
                                            <button
                                                onClick={() => setActiveTab('sitter')}
                                                className={`pb-4 px-2 font-medium transition-colors relative whitespace-nowrap ${
                                                    activeTab === 'sitter' ? 'text-orange-600' : 'text-gray-500 hover:text-gray-700'
                                                }`}
                                            >
                                                🧾 Sitter Notes
                                                {activeTab === 'sitter' && (
                                                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-600"></div>
                                                )}
                                            </button>
                                        </div>
                                    </div>

                                    {/* OVERVIEW TAB */}
                                    {activeTab === 'pets' && (
                                        <div className="bg-white rounded-b-lg shadow-md p-6 space-y-6">
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="text-sm font-medium text-gray-600">Species</label>
                                                    <p className="text-lg font-semibold">{selectedPet.species}</p>
                                                </div>
                                                <div>
                                                    <label className="text-sm font-medium text-gray-600">Breed</label>
                                                    <p className="text-lg font-semibold">{selectedPet.breed || 'Not specified'}</p>
                                                </div>
                                                <div>
                                                    <label className="text-sm font-medium text-gray-600">Age</label>
                                                    <p className="text-lg font-semibold">{selectedPet.age || 'Unknown'}</p>
                                                </div>
                                                <div>
                                                    <label className="text-sm font-medium text-gray-600">Gender</label>
                                                    <p className="text-lg font-semibold">{selectedPet.gender}</p>
                                                </div>
                                                {selectedPet.weight && (
                                                    <div>
                                                        <label className="text-sm font-medium text-gray-600">Weight</label>
                                                        <p className="text-lg font-semibold">{selectedPet.weight}</p>
                                                    </div>
                                                )}
                                                {selectedPet.color && (
                                                    <div>
                                                        <label className="text-sm font-medium text-gray-600">Color</label>
                                                        <p className="text-lg font-semibold">{selectedPet.color}</p>
                                                    </div>
                                                )}
                                            </div>

                                            {selectedPet.microchipId && (
                                                <div className="border-t pt-4">
                                                    <label className="text-sm font-medium text-gray-600 flex items-center">
                                                        <Shield className="w-4 h-4 mr-1" />
                                                        Microchip ID
                                                    </label>
                                                    <p className="text-lg font-semibold">{selectedPet.microchipId}</p>
                                                </div>
                                            )}

                                            {selectedPet.allergies && (
                                                <div className="border-t pt-4">
                                                    <label className="text-sm font-medium text-gray-600 flex items-center">
                                                        <AlertCircle className="w-4 h-4 mr-1 text-red-500" />
                                                        Allergies
                                                    </label>
                                                    <p className="text-gray-800">{selectedPet.allergies}</p>
                                                </div>
                                            )}

                                            {selectedPet.insuranceInfo && (
                                                <div className="border-t pt-4">
                                                    <label className="text-sm font-medium text-gray-600 flex items-center">
                                                        <Shield className="w-4 h-4 mr-1 text-blue-500" />
                                                        Insurance Information
                                                    </label>
                                                    <p className="text-gray-800">{selectedPet.insuranceInfo}</p>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* VET RECORDS TAB */}
                                    {activeTab === 'vet' && (
                                        <div className="bg-white rounded-b-lg shadow-md p-6">
                                            <div className="flex justify-between items-center mb-6">
                                                <h3 className="text-xl font-bold">Veterinary Records</h3>
                                                <button
                                                    onClick={() => {
                                                        setNewVetRecord({ ...newVetRecord, petId: selectedPet.id });
                                                        setShowAddVetModal(true);
                                                    }}
                                                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                                >
                                                    <Plus className="w-4 h-4" />
                                                    <span>Add Visit</span>
                                                </button>
                                            </div>

                                            {/* Vaccinations Section */}
                                            <div className="mb-6">
                                                <div className="flex justify-between items-center mb-4">
                                                    <h4 className="text-lg font-semibold flex items-center">
                                                        <Syringe className="w-5 h-5 mr-2 text-green-600" />
                                                        Vaccinations
                                                    </h4>
                                                    <button
                                                        onClick={() => {
                                                            setNewVaccination({ ...newVaccination, petId: selectedPet.id });
                                                            setShowAddVaccineModal(true);
                                                        }}
                                                        className="text-sm px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                                                    >
                                                        + Add Vaccine
                                                    </button>
                                                </div>
                                                <div className="space-y-2">
                                                    {selectedPetVaccinations.map(vaccine => (
                                                        <div key={vaccine.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                                                            <div className="flex-1">
                                                                <p className="font-semibold">{vaccine.vaccineName}</p>
                                                                <p className="text-sm text-gray-600">
                                                                    Given: {new Date(vaccine.dateGiven).toLocaleDateString()}
                                                                    {vaccine.nextDue && ` | Next: ${new Date(vaccine.nextDue).toLocaleDateString()}`}
                                                                </p>
                                                            </div>
                                                            <button
                                                                onClick={() => handleDeleteVaccination(vaccine.id)}
                                                                className="text-red-600 hover:text-red-800"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    ))}
                                                    {selectedPetVaccinations.length === 0 && (
                                                        <p className="text-gray-500 text-center py-4">No vaccinations recorded</p>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Vet Visits */}
                                            <div>
                                                <h4 className="text-lg font-semibold mb-4 flex items-center">
                                                    <Stethoscope className="w-5 h-5 mr-2 text-blue-600" />
                                                    Visit History
                                                </h4>
                                                <div className="space-y-4">
                                                    {selectedPetVetRecords.map(record => (
                                                        <div key={record.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                                                            <div className="flex justify-between items-start mb-2">
                                                                <div>
                                                                    <p className="font-semibold text-lg">{record.vetName}</p>
                                                                    <p className="text-sm text-gray-600">{new Date(record.visitDate).toLocaleDateString()}</p>
                                                                </div>
                                                                <button
                                                                    onClick={() => handleDeleteVetRecord(record.id)}
                                                                    className="text-red-600 hover:text-red-800"
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                </button>
                                                            </div>
                                                            <p className="text-sm"><strong>Reason:</strong> {record.reason}</p>
                                                            {record.notes && <p className="text-sm mt-2 text-gray-700">{record.notes}</p>}
                                                        </div>
                                                    ))}
                                                    {selectedPetVetRecords.length === 0 && (
                                                        <p className="text-gray-500 text-center py-8">No vet visits recorded</p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* FEEDING & CARE TAB */}
                                    {activeTab === 'feeding' && (
                                        <div className="bg-white rounded-b-lg shadow-md p-6">
                                            {/* Feeding Schedule */}
                                            <div className="mb-8">
                                                <div className="flex justify-between items-center mb-4">
                                                    <h3 className="text-xl font-bold flex items-center">
                                                        <Utensils className="w-6 h-6 mr-2 text-orange-600" />
                                                        Feeding Schedule
                                                    </h3>
                                                    <button
                                                        onClick={() => {
                                                            setNewFeeding({ ...newFeeding, petId: selectedPet.id });
                                                            setShowAddFeedingModal(true);
                                                        }}
                                                        className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                                                    >
                                                        + Add Feeding
                                                    </button>
                                                </div>
                                                <div className="space-y-3">
                                                    {selectedPetFeeding.map(feeding => (
                                                        <div key={feeding.id} className="flex items-center justify-between p-4 bg-orange-50 rounded-lg">
                                                            <div className="flex-1">
                                                                <p className="font-semibold">{feeding.time} - {feeding.foodType}</p>
                                                                <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600 mt-1">
                                                                    <span>{feeding.quantity} | {feeding.recurrence}</span>
                                                                    {feeding.assignedTo && (
                                                                        <div className="flex items-center gap-1">
                                                                            <span>|</span>
                                                                            <MemberAvatar name={feeding.assignedTo} size="xs" />
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <button
                                                                onClick={() => handleDeleteFeeding(feeding.id)}
                                                                className="text-red-600 hover:text-red-800"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    ))}
                                                    {selectedPetFeeding.length === 0 && (
                                                        <p className="text-gray-500 text-center py-4">No feeding schedule set</p>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Care Tasks */}
                                            <div>
                                                <div className="flex justify-between items-center mb-4">
                                                    <h3 className="text-xl font-bold flex items-center">
                                                        <Activity className="w-6 h-6 mr-2 text-pink-600" />
                                                        Care Tasks
                                                    </h3>
                                                    <button
                                                        onClick={() => {
                                                            setNewCareTask({ ...newCareTask, petId: selectedPet.id });
                                                            setShowAddCareTaskModal(true);
                                                        }}
                                                        className="px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700"
                                                    >
                                                        + Add Task
                                                    </button>
                                                </div>
                                                <div className="space-y-3">
                                                    {selectedPetCareTasks.map(task => (
                                                        <div key={task.id} className="flex items-center justify-between p-4 bg-pink-50 rounded-lg">
                                                            <div className="flex items-center space-x-3 flex-1">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={task.completed}
                                                                    onChange={() => handleToggleCareTask(task.id)}
                                                                    className="w-5 h-5 text-pink-600 rounded"
                                                                />
                                                                <div className="flex-1">
                                                                    <p className={`font-semibold ${task.completed ? 'line-through text-gray-500' : ''}`}>
                                                                        {task.taskType} - {task.time}
                                                                    </p>
                                                                    <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600 mt-1">
                                                                        <span>{task.duration && `${task.duration} | `}{task.recurrence}</span>
                                                                        {task.assignedTo && (
                                                                            <div className="flex items-center gap-1">
                                                                                <span>|</span>
                                                                                <MemberAvatar name={task.assignedTo} size="xs" />
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <button
                                                                onClick={() => handleDeleteCareTask(task.id)}
                                                                className="text-red-600 hover:text-red-800"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    ))}
                                                    {selectedPetCareTasks.length === 0 && (
                                                        <p className="text-gray-500 text-center py-4">No care tasks scheduled</p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* SITTER NOTES TAB */}
                                    {activeTab === 'sitter' && (
                                        <div className="bg-white rounded-b-lg shadow-md p-6">
                                            <div className="flex justify-between items-center mb-6">
                                                <h3 className="text-xl font-bold">Pet Sitter Instructions</h3>
                                                <div className="flex space-x-2">
                                                    <button
                                                        onClick={() => handleExportPetPassport(selectedPet)}
                                                        className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                                    >
                                                        <Printer className="w-4 h-4" />
                                                        <span>Export Pet Passport</span>
                                                    </button>
                                                    {!editingSitterNotes && (
                                                        <button
                                                            onClick={() => setEditingSitterNotes(true)}
                                                            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                                                        >
                                                            Edit Notes
                                                        </button>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="space-y-6">
                                                {[
                                                    { key: 'feedingInstructions', title: '🍖 Feeding Instructions', placeholder: 'Food type, portions, times, special dietary needs...' },
                                                    { key: 'medicationInstructions', title: '💊 Medication Instructions', placeholder: 'Medications, dosages, timing...' },
                                                    { key: 'behaviorNotes', title: '🐾 Behavior Notes', placeholder: 'Personality, likes/dislikes, triggers...' },
                                                    { key: 'emergencyContacts', title: '📞 Emergency Contacts', placeholder: 'Vet name, phone, emergency clinic...' },
                                                    { key: 'specialInstructions', title: '📝 Special Instructions', placeholder: 'Exercise needs, play preferences, bathroom routine...' }
                                                ].map(section => (
                                                    <div key={section.key} className="border-b pb-4">
                                                        <h4 className="font-semibold mb-2">{section.title}</h4>
                                                        {editingSitterNotes ? (
                                                            <textarea
                                                                value={sitterNotes[`${selectedPet.id}_${section.key}`] || ''}
                                                                onChange={(e) => setSitterNotes({
                                                                    ...sitterNotes,
                                                                    [`${selectedPet.id}_${section.key}`]: e.target.value
                                                                })}
                                                                rows={4}
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                                                                placeholder={section.placeholder}
                                                            />
                                                        ) : (
                                                            <p className="text-gray-700 whitespace-pre-wrap">
                                                                {sitterNotes[`${selectedPet.id}_${section.key}`] || 'No information provided'}
                                                            </p>
                                                        )}
                                                    </div>
                                                ))}

                                                {editingSitterNotes && (
                                                    <div className="flex justify-end space-x-3">
                                                        <button
                                                            onClick={() => {
                                                                setEditingSitterNotes(false);
                                                                loadSitterNotes();
                                                            }}
                                                            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                                                        >
                                                            Cancel
                                                        </button>
                                                        <button
                                                            onClick={handleSaveSitterNotes}
                                                            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                                                        >
                                                            Save Notes
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Add/Edit Pet Modal */}
            {showAddPetModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-white rounded-lg max-w-2xl w-full max-h-screen overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                            <h2 className="text-2xl font-bold">{editingPet ? 'Edit Pet' : 'Add New Pet'}</h2>
                            <button
                                onClick={() => {
                                    setShowAddPetModal(false);
                                    setEditingPet(null);
                                    resetPetForm();
                                }}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="p-6 space-y-4">
                            {/* Photo Upload */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Pet Photo</label>
                                <input
                                    ref={photoInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handlePhotoSelect}
                                    className="hidden"
                                />
                                <div className="flex items-center space-x-4">
                                    {newPet.photo ? (
                                        <img src={newPet.photo} alt="Pet" className="w-20 h-20 rounded-full object-cover" />
                                    ) : (
                                        <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center">
                                            <ImageIcon className="w-8 h-8 text-gray-400" />
                                        </div>
                                    )}
                                    <button
                                        onClick={() => photoInputRef.current?.click()}
                                        className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg border border-gray-300"
                                    >
                                        Choose Photo
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                                    <input
                                        type="text"
                                        value={newPet.name}
                                        onChange={(e) => setNewPet({ ...newPet, name: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                                        placeholder="Pet name"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Species</label>
                                    <select
                                        value={newPet.species}
                                        onChange={(e) => setNewPet({ ...newPet, species: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                                    >
                                        {species.map(s => (
                                            <option key={s} value={s}>{s}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Breed</label>
                                    <input
                                        type="text"
                                        value={newPet.breed}
                                        onChange={(e) => setNewPet({ ...newPet, breed: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                                        placeholder="Breed"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
                                    <input
                                        type="text"
                                        value={newPet.age}
                                        onChange={(e) => setNewPet({ ...newPet, age: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                                        placeholder="e.g., 2 years"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                                    <select
                                        value={newPet.gender}
                                        onChange={(e) => setNewPet({ ...newPet, gender: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                                    >
                                        <option value="Male">Male</option>
                                        <option value="Female">Female</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Weight</label>
                                    <input
                                        type="text"
                                        value={newPet.weight}
                                        onChange={(e) => setNewPet({ ...newPet, weight: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                                        placeholder="e.g., 15 kg"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                                    <input
                                        type="text"
                                        value={newPet.color}
                                        onChange={(e) => setNewPet({ ...newPet, color: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                                        placeholder="e.g., Brown"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Microchip ID</label>
                                    <input
                                        type="text"
                                        value={newPet.microchipId}
                                        onChange={(e) => setNewPet({ ...newPet, microchipId: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                                        placeholder="Microchip number"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Allergies</label>
                                <textarea
                                    value={newPet.allergies}
                                    onChange={(e) => setNewPet({ ...newPet, allergies: e.target.value })}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                                    placeholder="List any known allergies..."
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Insurance Information</label>
                                <textarea
                                    value={newPet.insuranceInfo}
                                    onChange={(e) => setNewPet({ ...newPet, insuranceInfo: e.target.value })}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                                    placeholder="Insurance provider, policy number, etc..."
                                />
                            </div>
                        </div>

                        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex justify-end space-x-3 border-t border-gray-200">
                            <button
                                onClick={() => {
                                    setShowAddPetModal(false);
                                    setEditingPet(null);
                                    resetPetForm();
                                }}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={editingPet ? handleUpdatePet : handleAddPet}
                                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                            >
                                {editingPet ? 'Update' : 'Add'} Pet
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Other modals available but keeping file size manageable - Add Vet, Vaccination, Feeding, Care Task modals can be added similarly */}
            
            {/* Premium Modal */}
            {showPremiumModal && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black bg-opacity-75">
                    <div className="relative rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col" style={{ background: 'transparent' }}>
                        {/* Close Button */}
                        <button
                            onClick={() => setShowPremiumModal(false)}
                            className="absolute top-4 right-4 z-50 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-colors"
                        >
                            <X className="w-6 h-6 text-gray-600" />
                        </button>
                        
                        {/* Premium Component */}
                        <Premium isModal={true} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default Pets;

